
import { 
  collection, 
  addDoc, 
  getDocs, 
  doc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  onSnapshot,
  setDoc,
  increment,
  getDoc,
  arrayUnion,
  arrayRemove,
  runTransaction
} from 'firebase/firestore';
import { db } from '../firebase';
import { Product, Order, UserProfile, WallPost, StoreSettings, DirectMessage, MessagePayload } from '../types';

// --- Users ---
export const createUserProfile = async (user: UserProfile) => {
  try {
    await setDoc(doc(db, "users", user.uid), user);
  } catch (e) {
    console.error("Error creating user profile: ", e);
  }
};

export const updateUserProfile = async (uid: string, data: Partial<UserProfile>) => {
  try {
    await updateDoc(doc(db, "users", uid), data);
  } catch (e) {
    console.error("Error updating user profile: ", e);
  }
};

export const getUserProfile = async (uid: string): Promise<UserProfile | null> => {
  try {
    const docRef = doc(db, "users", uid);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return docSnap.data() as UserProfile;
    }
    return null;
  } catch (e) {
    console.error("Error fetching user profile:", e);
    return null;
  }
};

export const getCustomers = async (): Promise<UserProfile[]> => {
  try {
    const q = query(collection(db, "users"), where("role", "==", "customer"));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => doc.data() as UserProfile);
  } catch (e) {
    console.error("Error fetching customers", e);
    return [];
  }
};

// --- Store Settings ---
export const getStoreSettings = async (): Promise<StoreSettings> => {
  try {
    const docRef = doc(db, "settings", "general");
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return docSnap.data() as StoreSettings;
    }
    return {};
  } catch (e) {
    console.error("Error fetching settings", e);
    return {};
  }
};

export const updateStoreSettings = async (settings: StoreSettings) => {
  await setDoc(doc(db, "settings", "general"), settings, { merge: true });
};

// --- Products ---
export const getProducts = async (): Promise<Product[]> => {
  const q = query(collection(db, "products"));
  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Product));
};

export const addProduct = async (product: Omit<Product, 'id'>) => {
  await addDoc(collection(db, "products"), product);
};

export const updateProduct = async (id: string, data: Partial<Product>) => {
  await updateDoc(doc(db, "products", id), data);
};

export const deleteProduct = async (id: string) => {
  await deleteDoc(doc(db, "products", id));
};

export const seedDatabase = async () => {
  const initialProducts: Omit<Product, 'id'>[] = [
    {
      name: "Galactic Espresso",
      description: "A strong, concentrated shot of dark matter coffee.",
      price: 120, 
      category: "coffee",
      imageUrl: "https://images.unsplash.com/photo-1514432324607-a09d9b4aefdd?auto=format&fit=crop&w=400&q=80",
      available: true,
      pointsEarned: 12
    },
    {
      name: "Lunar Latte",
      description: "Steamed milk with espresso and a dusting of stardust (cinnamon).",
      price: 180,
      category: "coffee",
      imageUrl: "https://images.unsplash.com/photo-1541167760496-1628856ab772?auto=format&fit=crop&w=400&q=80",
      available: true,
      pointsEarned: 18
    },
    {
      name: "Nebula Tea",
      description: "Color-changing butterfly pea flower tea with lemon.",
      price: 160,
      category: "tea",
      imageUrl: "https://images.unsplash.com/photo-1597481499750-3e6b22637e12?auto=format&fit=crop&w=400&q=80",
      available: true,
      pointsEarned: 16
    },
    {
      name: "Meteor Croissant",
      description: "Flaky, buttery pastry shaped like a crescent moon.",
      price: 95,
      category: "pastry",
      imageUrl: "https://images.unsplash.com/photo-1555507036-ab1f4038808a?auto=format&fit=crop&w=400&q=80",
      available: true,
      pointsEarned: 9
    }
  ];

  for (const p of initialProducts) {
    await addProduct(p);
  }
};

// --- Orders & Points ---
export const createOrder = async (order: Omit<Order, 'id'>) => {
  // Points are now only awarded when status is 'completed'
  await addDoc(collection(db, "orders"), {
    ...order,
    pointsAwarded: false
  });
};

export const redeemProduct = async (userId: string, userName: string, product: Product, pointsCost: number) => {
  const order: Omit<Order, 'id'> = {
    userId,
    customerName: userName,
    items: [{...product, quantity: 1, price: 0}], 
    total: 0,
    status: 'pending',
    createdAt: Date.now(),
    isRedemption: true
  };

  await addDoc(collection(db, "orders"), order);

  const userRef = doc(db, "users", userId);
  await updateDoc(userRef, {
    points: increment(-pointsCost)
  });
};

export const subscribeToOrders = (
  callback: (orders: Order[]) => void, 
  filterUser?: string
) => {
  let q;
  if (filterUser) {
    q = query(collection(db, "orders"), where("userId", "==", filterUser), orderBy("createdAt", "desc"));
  } else {
    q = query(collection(db, "orders"), orderBy("createdAt", "desc"));
  }

  return onSnapshot(q, (snapshot) => {
    const orders = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Order));
    callback(orders);
  }, (error) => {
    console.error("Error subscribing to orders:", error);
  });
};

export const updateOrderStatus = async (orderId: string, status: Order['status']) => {
  const orderRef = doc(db, "orders", orderId);

  // If completing, we need to check if we should award points using a transaction
  if (status === 'completed') {
    await runTransaction(db, async (transaction) => {
      const orderSnap = await transaction.get(orderRef);
      if (!orderSnap.exists()) {
        throw new Error("Order does not exist!");
      }

      const orderData = orderSnap.data() as Order;
      
      // Only award points if not already awarded and not a redemption
      if (!orderData.pointsAwarded && !orderData.isRedemption && orderData.userId) {
        let pointsEarned = 0;
        orderData.items.forEach(item => {
          const pts = item.pointsEarned !== undefined ? item.pointsEarned : Math.floor(item.price);
          pointsEarned += (pts * item.quantity);
        });

        if (pointsEarned > 0) {
          const userRef = doc(db, "users", orderData.userId);
          transaction.update(userRef, { points: increment(pointsEarned) });
          transaction.update(orderRef, { status: 'completed', pointsAwarded: true });
        } else {
          transaction.update(orderRef, { status: 'completed' });
        }
      } else {
        // Just update status if points already awarded or invalid
        transaction.update(orderRef, { status: 'completed' });
      }
    });
  } else {
    // Simple update for other statuses
    await updateDoc(orderRef, { status });
  }
};

// --- Moonlit Wall ---
export const addWallPost = async (post: Omit<WallPost, 'id' | 'likes' | 'likedBy'>) => {
  const fullPost = {
    ...post,
    likes: 0,
    likedBy: []
  };
  await addDoc(collection(db, "wall_posts"), fullPost);
};

export const likeWallPost = async (postId: string, userId: string, isLiked: boolean) => {
  const postRef = doc(db, "wall_posts", postId);
  if (isLiked) {
    // Unlike
    await updateDoc(postRef, {
      likes: increment(-1),
      likedBy: arrayRemove(userId)
    });
  } else {
    // Like
    await updateDoc(postRef, {
      likes: increment(1),
      likedBy: arrayUnion(userId)
    });
  }
};

export const subscribeToWallPosts = (callback: (posts: WallPost[]) => void) => {
  const q = query(collection(db, "wall_posts"), orderBy("createdAt", "desc"));
  return onSnapshot(q, (snapshot) => {
    const posts = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as WallPost));
    callback(posts);
  }, (error) => {
    console.error("Error subscribing to wall posts:", error);
  });
};

// --- Direct Messaging ---

// Send a message from Customer or Owner
export const sendMessage = async (chatId: string, customerName: string, message: MessagePayload) => {
  const chatRef = doc(db, "direct_messages", chatId);
  const chatSnap = await getDoc(chatRef);

  if (!chatSnap.exists()) {
    // Create new chat session
    const newChat: DirectMessage = {
      id: chatId,
      customerName: customerName,
      lastUpdated: Date.now(),
      messages: [message],
      unreadByOwner: message.role === 'customer',
      unreadByCustomer: message.role === 'owner'
    };
    await setDoc(chatRef, newChat);
  } else {
    // Update existing chat
    await updateDoc(chatRef, {
      messages: arrayUnion(message),
      lastUpdated: Date.now(),
      unreadByOwner: message.role === 'customer' ? true : chatSnap.data().unreadByOwner,
      unreadByCustomer: message.role === 'owner' ? true : chatSnap.data().unreadByCustomer
    });
  }
};

// Mark messages as read
export const markChatRead = async (chatId: string, readerRole: 'customer' | 'owner') => {
  const chatRef = doc(db, "direct_messages", chatId);
  const data = readerRole === 'owner' ? { unreadByOwner: false } : { unreadByCustomer: false };
  await updateDoc(chatRef, data);
};

// Subscribe to a specific chat (for Customer or selected Owner view)
export const subscribeToChat = (chatId: string, callback: (chat: DirectMessage | null) => void) => {
  return onSnapshot(doc(db, "direct_messages", chatId), (doc) => {
    if (doc.exists()) {
      callback({ id: doc.id, ...doc.data() } as DirectMessage);
    } else {
      callback(null);
    }
  }, (error) => {
    console.error("Error subscribing to single chat:", error);
  });
};

// Subscribe to all chats (for Owner list)
export const subscribeToAllChats = (callback: (chats: DirectMessage[]) => void) => {
  const q = query(collection(db, "direct_messages"), orderBy("lastUpdated", "desc"));
  return onSnapshot(q, (snapshot) => {
    const chats = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as DirectMessage));
    callback(chats);
  }, (error) => {
    console.error("Error subscribing to all chats:", error);
  });
};
